import React, { useContext, useMemo, useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc as firestoreDoc, onSnapshot, updateDoc } from 'firebase/firestore';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import Layout from '../../../components/Layout';
import { LockClosedIcon, CheckCircleIcon, DocumentTextIcon, SearchIcon, ExclamationCircleIcon } from '@heroicons/react/outline';
import { ChevronRightIcon } from '@heroicons/react/solid';
import Breadcrumb from '../../../components/Breadcrumb';
import { ModularPatientDataContext } from '../../../context/ModularPatientDataContext';
import { db, auth } from '../../../firebase';
import {
  STAGE_TO_REPORT_PREFIX,
  STAGE_TO_CLINICIAN_KEY,
  getEnabledAutismStages,
} from '../../../constants/adultAutismStages';
import {
  getPatientHeaderPathSegments,
  getPatientReportContentPathSegments,
} from '../../../constants/adultAutismFirestorePaths';
import { DocumentEmbedDialog } from '../8_diagnosticDecision/components/DocumentEmbedDialog';
import {
  AUTISM_STAGE_DOCUMENT_INFO,
  getDocumentActions,
  getAutismHistorySectionTitles,
  getAutismDocumentDisplayTitle,
} from '../../../utils/autismDocumentActions';
import { TriggerRegenerateAutismStage } from '../../../utils/TriggerRegenerateAutismStage';
import { TriggerRenderAutismPatientReport } from '../../../utils/TriggerRenderAutismPatientReport';
import { TriggerFetchLatestDocForDownload } from '../../../utils/TriggerFetchLatestDocForDownload';
import { getStaleAutismReportReset } from '../../../utils/processingStatusHelpers';
import { isAutismPatientFemale } from '../../../utils/conditionChecks';
import { isPdfReady, getPdfAvailability } from '../../../utils/pdfAvailability';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import DownloadDropdown from './components/DownloadDropdown';

/**
 * Autism Documents Dashboard
 *
 * Displays the report-section pipeline for an Adult Autism assessment. Each row
 * represents one assessment stage and shows its generation/approval status, the
 * assigned clinician, and an action button when applicable.
 *
 * This dashboard mirrors the ADHD DocumentsDashboard visually but is considerably
 * simpler because the Autism pathway has:
 * - No cover page, rating scales, QbCheck, or QbTest sections
 * - No bundle/CAR download
 * - Only 5 report stages, two of which are placeholder ("Coming Soon")
 *
 * Data comes from ModularPatientDataContext, which provides the patient header
 * (with clinician assignments) and the reportContent subcollection docs (with
 * generation/approval flags and LLM outputs).
 *
 * For stages with a render pipeline (currently History and SSI), the patient
 * header listener also drives preview/finalising behaviour. Every dashboard
 * stage has a reportContent/{stage} onSnapshot listener so generation and
 * approval status (including Observation, MDT Review, and Feedback Session)
 * update as soon as the Cloud Function writes outputs — the one-shot context
 * load alone would leave those rows stuck on "Locked" after Finish flows.
 *
 * Visual parity note: the action set and styling deliberately match the ADHD
 * (TOC) Documents Dashboard "Report Builder" rows — rows tint amber while a
 * render is in flight, "Review AI Outputs" / "Review Paragraphs" use the same
 * button styles, and "Preview" uses the magnifying-glass icon. There is no
 * "Download" button here (downloading is done from inside the Preview dialog),
 * exactly as on the ADHD dashboard.
 */
export default function DocumentsDashboard_Autism() {
  const { patientId } = useParams();
  const navigate = useNavigate();

  const { header, reportContent, assessmentStages } = useContext(ModularPatientDataContext);

  // Workflow config — used to exclude the Risk Assessment section from bulk
  // regeneration when this patient's workflow doesn't include it (e.g. TOC
  // autism, ciFlow.riskAssessment: 'disabled'), so we never trigger a suppressed
  // paragraph. Absent -> enabled, so legacy patients still regenerate risk.
  const { claims } = useAuth();
  const wf = useWorkflowConfig(header, claims);

  // ── History "Regenerate AI outputs" control state ───────────────────────────
  // Only relevant for patients whose forms were unlocked post-assessment: when a
  // patient/informant updates their pre-assessment questionnaires after History
  // paragraphs were generated, the clinician needs a way to re-run generation on
  // the latest inputs. Mirrors the ADHD dashboard's CI/SSI regenerate controls.
  const [showHistoryRegenerateDialog, setShowHistoryRegenerateDialog] = useState(false);
  const [isHistoryRegenerating, setIsHistoryRegenerating] = useState(false);

  // The History sections that have an AI paragraph (derived from the loaded
  // history stage data so they always match the backend) and the patient
  // conditions that gate optional paragraphs (e.g. Women's Health for females).
  const historySectionTitles = useMemo(
    () => getAutismHistorySectionTitles(assessmentStages, wf.isCIRiskAssessmentEnabled),
    [assessmentStages, wf.isCIRiskAssessmentEnabled],
  );
  const regenerateConditions = useMemo(
    () => (isAutismPatientFemale(assessmentStages) ? ['patientIsFemale'] : []),
    [assessmentStages],
  );

  // Patient-level override flag (set when an admin unlocks a patient's forms
  // post-assessment). The regenerate control is hidden unless it is explicitly
  // true, matching the ADHD SSI regenerate gating.
  const formsUnlocked = header?.formsUnlocked === true;

  const handleConfirmHistoryRegenerate = useCallback(async () => {
    if (isHistoryRegenerating) return;
    setIsHistoryRegenerating(true);
    try {
      await TriggerRegenerateAutismStage({
        patientId,
        clinicianId: auth.currentUser?.uid,
        stageKey: 'history',
        sectionTitles: historySectionTitles,
        conditions: regenerateConditions,
      });
      setShowHistoryRegenerateDialog(false);
    } catch (e) {
      console.error('Autism History regeneration failed:', e);
    } finally {
      setIsHistoryRegenerating(false);
    }
  }, [isHistoryRegenerating, patientId, historySectionTitles, regenerateConditions]);

  // ── Live data for rendered documents ───────────────────────────────────────
  // The modular context loads reportContent via a one-shot getDocs, so it will
  // NOT observe Cloud Function writes (LLM paragraph generation or PDF render)
  // that happen after the patient was opened. We attach scoped onSnapshot
  // listeners so the dashboard reflects changes in real time:
  //   - the patient header (patients/{id}) for the documents.* metadata, and
  //   - one reportContent/{stage} listener per dashboard stage (every key in
  //     STAGE_TO_REPORT_PREFIX) for generation/approval flags and — for stages
  //     with a render pipeline (AUTISM_STAGE_DOCUMENT_INFO) — live processing
  //     status while a PDF is finalising.
  // Without the reportContent listeners, stages like Observation would stay
  // "Locked" after Finish → Generate Report because the context snapshot was
  // taken before generation completed. All listeners are read-only and
  // unsubscribe on unmount / patient change.
  const [liveDocuments, setLiveDocuments] = useState(null);
  // Patient report status lives directly on the patient header (not on a
  // reportContent doc), so we extract it alongside the documents map.
  const [livePatientReportStatus, setLivePatientReportStatus] = useState(null);
  // Keyed by stageKey -> reportContent doc data (or null). Covers every stage
  // whose row status is derived from reportContent (see STAGE_TO_REPORT_PREFIX).
  const [liveReportContent, setLiveReportContent] = useState({});

  useEffect(() => {
    if (!patientId) return undefined;
    const headerRef = firestoreDoc(db, ...getPatientHeaderPathSegments(patientId));
    const unsub = onSnapshot(
      headerRef,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setLiveDocuments(data.documents || null);
          setLivePatientReportStatus(data.autismPatientReport || null);

          // Self-heal a stuck patient-report render. Because there is no manual
          // "Generate" button anymore, a render that dies mid-flight (CF crash /
          // cold-start timeout) would otherwise strand the row on "Finalising...".
          // If the dashboard is open when that happens, flip the status to 'error'
          // (after the threshold) so the clinician's "Try Again" retry is reachable.
          // The backend reconcile sweep is the primary recovery for the
          // no-one-watching case; this is the fast in-tab fallback and sits after
          // that sweep's window (15 min > 12 min), so replay gets first chance.
          const reset = getStaleAutismReportReset(data.autismPatientReport);
          if (Object.keys(reset).length > 0) {
            console.warn('Auto-resetting stale Autism patient report processing status:', reset);
            updateDoc(headerRef, reset).catch((e) =>
              console.error('Failed to auto-reset stale Autism PR processing:', e),
            );
          }
        } else {
          setLiveDocuments(null);
          setLivePatientReportStatus(null);
        }
      },
      (err) => console.error('Autism documents header listener error:', err),
    );
    return () => unsub();
  }, [patientId]);

  useEffect(() => {
    if (!patientId) return undefined;
    // Reset on patient change so stale data from a previous patient can't leak.
    setLiveReportContent({});
    // Listen to all report stages shown on this dashboard, not only those with
    // a PDF render pipeline — generation flags and outputs live on the same docs.
    const trackedReportStages = Object.keys(STAGE_TO_REPORT_PREFIX);
    const unsubs = trackedReportStages.map((stageKey) => {
      const ref = firestoreDoc(db, ...getPatientReportContentPathSegments(patientId, stageKey));
      return onSnapshot(
        ref,
        (snap) => setLiveReportContent((prev) => ({
          ...prev,
          [stageKey]: snap.exists() ? snap.data() : null,
        })),
        (err) => console.error(`Autism ${stageKey} reportContent listener error:`, err),
      );
    });
    return () => unsubs.forEach((unsub) => unsub());
  }, [patientId]);

  // ── Preview dialog state ───────────────────────────────────────────────────
  // A single shared DocumentEmbedDialog renders the selected report inside an
  // iframe (reusing the ADHD embed flow). We track which report stage is open.
  const [previewReportStage, setPreviewReportStage] = useState(null);
  const [previewLastGenerated, setPreviewLastGenerated] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const handleOpenPreview = useCallback((reportStage, lastGenerated) => {
    setPreviewReportStage(reportStage);
    setPreviewLastGenerated(lastGenerated || null);
    setIsPreviewOpen(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setIsPreviewOpen(false);
    setPreviewReportStage(null);
    setPreviewLastGenerated(null);
  }, []);

  // ── Patient report download ─────────────────────────────────────────────────
  // Downloads the finalised Adult Autism Patient Report as PDF or DOCX via the
  // shared TriggerFetchLatestDocForDownload flow (same as the ADHD dashboard).
  // The reportStage string maps to documents.autismPatientReport on the backend.
  const [isPatientReportDownloading, setIsPatientReportDownloading] = useState(false);

  const handleDownloadAutismPatientReport = useCallback(async (format) => {
    try {
      setIsPatientReportDownloading(true);
      const result = await TriggerFetchLatestDocForDownload({
        reportStage: 'Autism Patient Report',
        clinicianId: auth.currentUser?.uid,
        patientId,
        fileFormat: format,
      });
      if (!result.success) {
        console.error('Autism patient report download failed:', result.error);
        alert('Failed to download report: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error downloading Autism patient report:', error);
      alert('Error downloading report: ' + (error.message || 'Unknown error'));
    } finally {
      setIsPatientReportDownloading(false);
    }
  }, [patientId]);

  /**
   * Derive the current status of a report stage from its reportContent document.
   *
   * Status flow:
   *   locked → ready-for-review → approved
   *
   * - `locked`: No reportContent doc exists yet, or the doc has no generation/output
   *   data. The Cloud Function that runs LLM paragraph generation hasn't been triggered
   *   or hasn't completed for this stage.
   *
   * - `ready-for-review`: The LLM has generated outputs (either the explicit
   *   `${prefix}ReportGenerated` flag is true, or the `outputs` map is populated).
   *   The clinician needs to review and approve the AI-drafted paragraphs.
   *
   * - `approved`: The clinician has reviewed all paragraphs and clicked "Approve".
   *   The `${prefix}ReportApproved` flag is set to true.
   */
  const getStatus = useCallback((stageKey) => {
    // Prefer the live snapshot (for renderable stages with a listener) so
    // approval/generation flips reflect immediately; fall back to the one-shot
    // context doc for stages without a dedicated listener.
    const doc = liveReportContent?.[stageKey] || reportContent?.[stageKey];
    if (!doc) return 'locked';

    const prefix = STAGE_TO_REPORT_PREFIX[stageKey];
    const isApproved = doc[`${prefix}ReportApproved`] === true;
    const isGenerated = doc[`${prefix}ReportGenerated`] === true;
    const hasOutputs = doc.outputs && Object.keys(doc.outputs).length > 0;

    if (isApproved) return 'approved';
    if (isGenerated || hasOutputs) return 'ready-for-review';
    return 'locked';
  }, [reportContent, liveReportContent]);

  /**
   * Build the list of section descriptors for all 5 report stages.
   *
   * Rows 1-3 (history, semiStructuredInterview, observation) and row 4
   * (diagnosticDecision) are active — they have review routes and their status
   * is derived from reportContent.
   *
   * Row 5 (postAssessmentDiscussion) is a placeholder — it always shows a
   * locked/coming-soon state regardless of any reportContent data, because
   * that assessment module hasn't been built yet. Once implemented, remove the
   * `isPlaceholder` flag to activate it.
   */
  const sections = useMemo(() => {
    const clinicians = header?.clinicians || {};
    // Stages that actually apply to this patient's workflow. Orgs that disable
    // Behavioural Observation (e.g. LMP) drop the OBS row entirely; the Patient
    // Report "all sections approved" gate below then counts only the remaining
    // rows, so it no longer waits on an OBS report that will never be produced.
    const enabledStages = getEnabledAutismStages(wf);

    // Build the rendered-document wiring for a stage that has a render pipeline
    // (an entry in AUTISM_STAGE_DOCUMENT_INFO). embedReportStage drives
    // preview/download; documentMeta + processingStatus drive button visibility
    // and the live spinner. Returns empty fields for non-renderable stages.
    const getRenderFields = (stageKey) => {
      const info = AUTISM_STAGE_DOCUMENT_INFO[stageKey];
      if (!info) return {};
      const prefix = STAGE_TO_REPORT_PREFIX[stageKey];
      return {
        embedReportStage: info.reportStage,
        documentMeta: liveDocuments?.[info.docKey] || null,
        processingStatus: liveReportContent?.[stageKey]?.[`${prefix}ReportProcessingStatus`],
      };
    };

    return [
      {
        stageKey: 'history',
        label: 'History',
        order: 1,
        status: getStatus('history'),
        route: `/patient/${patientId}/review-document-ci-autism`,
        isPlaceholder: false,
        ...getRenderFields('history'),
        reviewer: {
          name: clinicians[STAGE_TO_CLINICIAN_KEY.history]?.clinicianName || 'Unassigned',
          id: clinicians[STAGE_TO_CLINICIAN_KEY.history]?.clinicianId || null,
        },
      },
      {
        stageKey: 'semiStructuredInterview',
        label: 'Semi-Structured Interview',
        order: 2,
        status: getStatus('semiStructuredInterview'),
        route: `/patient/${patientId}/review-document-ssi-autism`,
        isPlaceholder: false,
        ...getRenderFields('semiStructuredInterview'),
        reviewer: {
          name: clinicians[STAGE_TO_CLINICIAN_KEY.semiStructuredInterview]?.clinicianName || 'Unassigned',
          id: clinicians[STAGE_TO_CLINICIAN_KEY.semiStructuredInterview]?.clinicianId || null,
        },
      },
      {
        stageKey: 'observation',
        label: 'Behavioural Observation',
        order: 3,
        status: getStatus('observation'),
        route: `/patient/${patientId}/review-document-obs-autism`,
        isPlaceholder: false,
        ...getRenderFields('observation'),
        reviewer: {
          name: clinicians[STAGE_TO_CLINICIAN_KEY.observation]?.clinicianName || 'Unassigned',
          id: clinicians[STAGE_TO_CLINICIAN_KEY.observation]?.clinicianId || null,
        },
      },
      {
        stageKey: 'diagnosticDecision',
        label: 'MDT Review',
        order: 4,
        status: getStatus('diagnosticDecision'),
        route: `/patient/${patientId}/review-document-dd-autism`,
        isPlaceholder: false,
        ...getRenderFields('diagnosticDecision'),
        reviewer: {
          name: clinicians[STAGE_TO_CLINICIAN_KEY.diagnosticDecision]?.clinicianName || 'Unassigned',
          id: clinicians[STAGE_TO_CLINICIAN_KEY.diagnosticDecision]?.clinicianId || null,
        },
      },
      {
        stageKey: 'postAssessmentDiscussion',
        label: 'Feedback Session',
        order: 5,
        status: getStatus('postAssessmentDiscussion'),
        route: `/patient/${patientId}/review-document-pad-autism`,
        isPlaceholder: false,
        ...getRenderFields('postAssessmentDiscussion'),
        reviewer: {
          name: clinicians[STAGE_TO_CLINICIAN_KEY.postAssessmentDiscussion]?.clinicianName || 'Unassigned',
          id: clinicians[STAGE_TO_CLINICIAN_KEY.postAssessmentDiscussion]?.clinicianId || null,
        },
      },
    ].filter((section) => enabledStages.includes(section.stageKey));
  }, [header, patientId, getStatus, liveDocuments, liveReportContent, wf]);

  // ── Styling helpers ────────────────────────────────────────────────
  // These mirror the ADHD dashboard's colour scheme exactly: amber while a
  // render is finalising, grey for locked, violet for ready-for-review, green
  // for approved. Placeholder rows always use the grey (locked) treatment.
  // The `isProcessing` flag takes precedence over the report status so a row
  // turns amber the instant a render starts, matching the ADHD "Finalising"
  // treatment.

  /**
   * Returns the Tailwind background class for a section card. While a render is
   * in flight the row tints amber (parity with ADHD); otherwise it tints to
   * match the report status so the clinician can scan the pipeline at a glance.
   */
  const getBackgroundClass = useCallback((status, isProcessing, isPending, isError) => {
    if (isProcessing || isPending) return 'bg-amber-50 hover:bg-amber-100';
    // Red for a failed render, matching the Patient Report row below.
    if (isError) return 'bg-red-50 hover:bg-red-100';
    switch (status) {
      case 'approved':
        return 'bg-green-50 hover:bg-green-100';
      case 'ready-for-review':
        return 'bg-violet-50 hover:bg-violet-100';
      default:
        return 'bg-gray-50 hover:bg-gray-100';
    }
  }, []);

  /**
   * Returns the Tailwind classes for the numbered circle indicator beside
   * each row. Amber while finalising or preparing PDF, red after a failed
   * render; otherwise matches the section's status.
   */
  const getCircleClass = useCallback((status, isProcessing, isPending, isError) => {
    if (isProcessing || isPending) return 'bg-amber-50 border-amber-200 text-amber-700';
    if (isError) return 'bg-red-50 border-red-200 text-red-700';
    switch (status) {
      case 'approved':
        return 'bg-green-50 border-green-200 text-green-700';
      case 'ready-for-review':
        return 'bg-violet-50 border-violet-200 text-violet-700';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-700';
    }
  }, []);

  /**
   * Returns the small status indicator displayed inline with the section title.
   * While finalising we show an amber spinner + "Finalising..."; while the PDF
   * is being prepared in the background we show "Preparing PDF"; after a failed
   * render we show the red error indicator (same icon + copy as the Patient
   * Report row -- the route back is "Review Paragraphs", which re-renders on
   * approval); otherwise: locked -> padlock, ready-for-review -> document
   * icon, approved -> tick.
   */
  const getStatusIcon = useCallback((status, isProcessing, isPending, isError) => {
    if (isProcessing) {
      return (
        <div className="flex items-center gap-2 text-amber-600">
          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-sm font-medium">Finalising...</span>
        </div>
      );
    }
    if (isPending) {
      return (
        <div className="flex items-center gap-2 text-amber-600">
          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-sm font-medium">Preparing PDF</span>
        </div>
      );
    }
    if (isError) {
      return (
        <div className="flex items-center gap-2 text-red-600">
          <ExclamationCircleIcon className="h-5 w-5" />
          <span className="text-sm font-medium">An error occurred. Please try again.</span>
        </div>
      );
    }
    switch (status) {
      case 'locked':
        return <LockClosedIcon className="w-5 h-5 text-gray-400" />;
      case 'ready-for-review':
        return <DocumentTextIcon className="w-5 h-5 text-violet-600" />;
      case 'approved':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      default:
        return null;
    }
  }, []);

  /**
   * Returns the human-readable status label shown below the section title.
   * Placeholder rows override this with "Coming Soon" regardless of status.
   */
  const getStatusText = useCallback((section) => {
    if (section.isPlaceholder) return 'Coming Soon';

    switch (section.status) {
      case 'approved':
        return 'Approved';
      case 'ready-for-review':
        return 'Ready for Review';
      case 'locked':
      default:
        return 'Locked';
    }
  }, []);

  /**
   * Returns contextual helper text for the clinician badge area. Shows who
   * the section is assigned to and what the current assignment means in the
   * context of the report workflow (assigned / ready for review / approved).
   */
  const getAssignmentLabel = useCallback((section) => {
    if (section.isPlaceholder) return 'Assigned to';

    switch (section.status) {
      case 'approved':
        return 'AI outputs last approved by';
      case 'ready-for-review':
        return 'AI outputs ready for review by';
      case 'locked':
      default:
        return 'Assigned to';
    }
  }, []);

  return (
    <Layout activePage="dashboard">
      <Breadcrumb
        tupleList={[
          ['Patient Overview', `/patient/${patientId}/adult-autism`],
          ['Autism Assessment', `/patient/${patientId}/adult-autism`],
          ['Documents'],
        ]}
      />

      {/* Patient Info Panel — shows patient name, email, and a quick link back
          to the Autism patient overview. Matches the ADHD dashboard's header. */}
      {header && (
        <div className="pl-8 pr-8">
          <div className="w-full px-6 py-5 bg-gray-50 rounded-xl border border-gray-200 justify-start items-center gap-14 inline-flex text-left mb-4">
            <div className="grow shrink basis-0 h-16 justify-start items-center gap-5 flex">
              <div className="grow shrink basis-0 flex-col justify-start items-start gap-1 inline-flex">
                <div className="self-stretch text-gray-900 text-3xl font-semibold leading-9">
                  {header.firstName} {header.lastName}
                </div>
                <div className="self-stretch text-slate-800 text-base font-normal leading-normal">
                  {header.email}
                </div>
              </div>
            </div>
            <div className="flex justify-start items-center gap-3">
              <Link
                to={`/patient/${patientId}/adult-autism`}
                className="px-4 py-2 font-bold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 inline-flex items-center gap-1"
              >
                Go to Patient Overview
                <ChevronRightIcon className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="flex-grow gap-4">
        {/* Report Sections Panel — a single white card containing all 5 assessment
            stages as a vertical pipeline with numbered circles and connecting lines. */}
        <div className="max-w-7xl p-8 mx-auto text-left bg-white rounded-xl shadow-sm border transition-all duration-200">
          {/* Panel title */}
          <div className="flex items-center justify-between ml-4 mb-2">
            <h1 className="text-2xl font-bold text-gray-900">
              Report Builder
            </h1>
          </div>

          {/* Connected section pipeline */}
          <div className="relative">
            {/* Vertical connecting line running down the left edge between the
                numbered circles. Positioned to start at the first circle's
                vertical centre and end at the last circle's centre. */}
            <div className="absolute left-[40px] top-12 bottom-12 w-0.5 bg-gray-200"></div>

            <div>
              {sections.map((section) => {
                // Resolve preview/download/spinner visibility for sections that
                // have a render pipeline (History, SSI). Placeholder and
                // not-yet-wired sections resolve to all-false (no actions).
                const docActions = section.embedReportStage
                  ? getDocumentActions({
                      status: section.status,
                      processingStatus: section.processingStatus,
                      documentMeta: section.documentMeta,
                    })
                  : { isProcessing: false, isPending: false, isError: false, hasRenderedDoc: false, showPreview: false, showDownload: false };

                return (
                <div key={section.stageKey} className="relative">
                  <div
                    className={`
                      w-full flex items-center gap-6 my-1 py-2 px-4 rounded-lg transition-all duration-200
                      ${getBackgroundClass(section.status, docActions.isProcessing, docActions.isPending, docActions.isError)}
                    `}
                  >
                    {/* Numbered circle — sits on top of the connecting line (z-10)
                        and is coloured to match the section's status. */}
                    <div
                      className={`
                        relative z-10 flex-shrink-0 w-12 h-12 rounded-full border-2
                        flex items-center justify-center font-semibold text-lg
                        ${getCircleClass(section.status, docActions.isProcessing, docActions.isPending, docActions.isError)}
                      `}
                    >
                      {section.order}
                    </div>

                    {/* Section info: title, status, and clinician badge */}
                    <div className="flex-1 flex items-center justify-between">
                      <div>
                        {/* Row 1: section label + inline status icon */}
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {section.label}
                          </h3>
                          {getStatusIcon(section.status, docActions.isProcessing, docActions.isPending, docActions.isError)}
                        </div>

                        {/* Row 2: status text + reviewer badge */}
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-sm italic text-gray-700">
                            {section.isPlaceholder
                              ? 'Coming Soon'
                              : `${getAssignmentLabel(section)}`}
                          </p>
                          {/* Clinician badge — shows assigned clinician name in a
                              pill-shaped chip. Placeholder rows always show "Unassigned". */}
                          {!section.isPlaceholder && (
                            <div className="inline-flex border items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700 transition-colors duration-200">
                              <span>{section.reviewer.name}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Action button area — only active (non-placeholder) sections
                          in ready-for-review or approved status show a button. Locked
                          active sections have no action because the LLM hasn't generated
                          outputs yet. Placeholder rows never have actions. */}
                      <div className="flex items-center gap-3">
                        {/* Explanatory helper text for locked active sections */}
                        {!section.isPlaceholder && section.status === 'locked' && (
                          <div className="text-xs text-gray-500 italic">
                            Available after assessment module is completed
                          </div>
                        )}

                        {/* "Review AI Outputs" — shown when outputs have been generated
                            and are awaiting clinician review / approval. Matches the
                            ADHD "Review AI Outputs" button (violet, filled).

                            Only the clinician assigned as primary for this module can
                            review / edit / approve the AI paragraphs (same gate as the
                            ADHD dashboard: section.reviewer.id === current user). Other
                            providers with dashboard access can still Preview / download
                            the rendered PDF but must not enter the review workflow. */}
                        {!section.isPlaceholder && section.status === 'ready-for-review' && section.route &&
                          section.reviewer.id === auth.currentUser?.uid && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(section.route);
                            }}
                            className="flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200 bg-violet-800 text-white hover:bg-violet-700"
                          >
                            Review AI Outputs
                          </button>
                        )}

                        {/* "Review Paragraphs" — shown after approval so the clinician
                            can revisit and re-edit previously approved paragraphs.
                            Rendered BEFORE Preview so the approved-row layout reads
                            "Review Paragraphs | Preview", matching the ADHD dashboard.
                            Same primary-clinician gate as "Review AI Outputs". */}
                        {!section.isPlaceholder && section.status === 'approved' && section.route &&
                          section.reviewer.id === auth.currentUser?.uid && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(section.route);
                            }}
                            className="flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200 text-gray-900 border border-gray-300 hover:bg-green-200"
                          >
                            Review Paragraphs
                          </button>
                        )}

                        {/* "Preview" — opens the rendered PDF in the embed dialog.
                            Only when approved + a rendered PDF exists + not finalising
                            (the finalising state shows the amber spinner in the title
                            row instead). Uses the magnifying-glass icon and the exact
                            ADHD Preview button styling. There is intentionally no
                            separate Download button — downloads happen inside the
                            preview dialog, matching the ADHD dashboard. */}
                        {docActions.showPreview && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenPreview(section.embedReportStage, section.documentMeta?.lastGenerated);
                            }}
                            className="text-gray-900 border border-gray-300 hover:bg-gray-100 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200"
                          >
                            <SearchIcon className="w-4 h-4 mr-1.5" />
                            Preview
                          </button>
                        )}

                        {/* "Regenerate AI outputs" — History only, and only when
                            the patient's forms were unlocked post-assessment so
                            updated questionnaire inputs can feed fresh paragraphs.
                            Restricted to the assigned History clinician and hidden
                            while locked (nothing generated yet). Mirrors the ADHD
                            dashboard's CI/SSI regenerate control. */}
                        {section.stageKey === 'history' && formsUnlocked &&
                          section.status !== 'locked' &&
                          section.reviewer.id === auth.currentUser?.uid && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowHistoryRegenerateDialog(true);
                              }}
                              disabled={docActions.isProcessing || isHistoryRegenerating}
                              className={`
                                flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200
                                ${docActions.isProcessing || isHistoryRegenerating
                                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                  : 'text-gray-900 border border-gray-300 hover:bg-red-600 hover:text-white'}
                              `}
                            >
                              Regenerate AI outputs
                            </button>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── Patient Report Panel ────────────────────────────────────────────────
           Separate from the 5-stage Assessment Report pipeline above. The patient
           report aggregates all 5 stages into a single patient-facing PDF. It can
           only be generated once all stages are approved. Processing status and
           document metadata live on the patient header (autismPatientReport.*
           and documents.autismPatientReport), not on a reportContent document. */}
      {(() => {
        const allStagesApproved = sections.every((s) => s.status === 'approved');
        const prInfo = AUTISM_STAGE_DOCUMENT_INFO.patientReport;
        const prDocMeta = liveDocuments?.[prInfo?.docKey] || null;
        const prProcessingStatus = livePatientReportStatus?.prReportProcessingStatus || null;
        const prIsProcessing = prProcessingStatus === 'processing';
        const prPdfAvailability = getPdfAvailability(prDocMeta);
        // isPending: the report rendered but the PDF is being retried in the
        // background. Row shows amber "Preparing PDF" with disabled actions.
        const prIsPending = !prIsProcessing && prPdfAvailability === 'pending';
        // Error comes from either the header status (render crashed) or the
        // document entry (PDF failed for good, including after the background
        // retry gave up). In-flight and pending states take precedence.
        const prIsError = !prIsProcessing && !prIsPending
          && (prProcessingStatus === 'error' || prPdfAvailability === 'error');
        const prHasRenderedDoc = isPdfReady(prDocMeta);
        const prShowPreview = allStagesApproved && prHasRenderedDoc && !prIsProcessing && !prIsPending;
        // Download available whenever a rendered PDF exists and nothing is in flight.
        const prShowDownload = prShowPreview;

        return (
          <div className="max-w-7xl p-8 mx-auto text-left bg-white rounded-xl shadow-sm border transition-all duration-200 mt-4">
            <div className="flex items-center justify-between ml-4 mb-2">
              <h1 className="text-2xl font-bold text-gray-900">
                Patient Report
              </h1>
            </div>

            <div
              className={`
                w-full flex items-center gap-6 my-1 py-2 px-4 rounded-lg transition-all duration-200
                ${prIsProcessing || prIsPending
                  ? 'bg-amber-50 hover:bg-amber-100'
                  : prIsError
                    ? 'bg-red-50 hover:bg-red-100'
                    : prHasRenderedDoc
                      ? 'bg-green-50 hover:bg-green-100'
                      : allStagesApproved
                        ? 'bg-gray-50 hover:bg-gray-100'
                        : 'bg-gray-50'}
              `}
            >
              {/* Document icon (no numbered circle for the patient report row) */}
              <div
                className={`
                  relative z-10 flex-shrink-0 w-12 h-12 rounded-full border-2
                  flex items-center justify-center
                  ${prIsProcessing || prIsPending
                    ? 'bg-amber-50 border-amber-200 text-amber-700'
                    : prIsError
                      ? 'bg-red-50 border-red-200 text-red-700'
                      : prHasRenderedDoc
                        ? 'bg-green-50 border-green-200 text-green-700'
                        : 'bg-gray-50 border-gray-200 text-gray-700'}
                `}
              >
                <DocumentTextIcon className="w-6 h-6" />
              </div>

              <div className="flex-1 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Adult Autism Patient Report
                    </h3>
                    {prIsProcessing && (
                      <div className="flex items-center gap-2 text-amber-600">
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm font-medium">Finalising...</span>
                      </div>
                    )}
                    {!prIsProcessing && prIsPending && (
                      <div className="flex items-center gap-2 text-amber-600">
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm font-medium">Preparing PDF</span>
                      </div>
                    )}
                    {!prIsProcessing && !prIsPending && prIsError && (
                      <div className="flex items-center gap-2 text-red-600">
                        <ExclamationCircleIcon className="h-5 w-5" />
                        <span className="text-sm font-medium">An error occurred. Please try again.</span>
                      </div>
                    )}
                    {!prIsProcessing && !prIsPending && !prIsError && prHasRenderedDoc && (
                      <CheckCircleIcon className="w-5 h-5 text-green-500" />
                    )}
                  </div>
                  <p className="text-sm italic text-gray-700 mt-1">
                    {!allStagesApproved
                      ? `Available after all ${sections.length} assessment report sections are approved`
                      : prIsProcessing
                        ? 'Finalising the patient report — this can take a few minutes.'
                        : prIsPending
                          ? 'The PDF is being prepared in the background. This will update automatically.'
                          : prHasRenderedDoc
                            ? 'Report generated'
                            : 'All sections approved — the patient report finalises automatically.'}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  {/* The patient report finalises automatically once all five
                      sections are approved (and re-finalises on any re-approval),
                      so there is deliberately no manual Generate/Re-generate button.
                      The only manual control is an error-recovery retry. */}

                  {/* Try Again — recovery path when a render failed. Re-invokes the
                      render CF directly (this is a retry, not a manual generate). */}
                  {prIsError && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        await TriggerRenderAutismPatientReport({
                          patientId,
                          clinicianId: auth.currentUser?.uid,
                        });
                      }}
                      className="bg-white text-primary border border-primary hover:bg-primary/5 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200"
                    >
                      Try again
                    </button>
                  )}

                  {/* Download (PDF/DOCX) — shown once a rendered report exists and
                      nothing is in flight. Uses the shared purple dropdown. */}
                  {prShowDownload && (
                    <DownloadDropdown
                      onDownload={handleDownloadAutismPatientReport}
                      isDownloading={isPatientReportDownloading}
                      label="Download Report"
                    />
                  )}

                  {/* Preview — when generated doc exists and not processing */}
                  {prShowPreview && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenPreview(prInfo.reportStage, prDocMeta?.lastGenerated);
                      }}
                      className="text-gray-900 border border-gray-300 hover:bg-gray-100 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200"
                    >
                      <SearchIcon className="w-4 h-4 mr-1.5" />
                      Preview
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Regenerate History AI outputs — confirmation dialog. Gated on the same
          formsUnlocked flag as the trigger button so it can never be opened for
          patients whose forms were not unlocked. Wording mirrors the ADHD
          Clinical Interview regenerate dialog. */}
      {formsUnlocked && (
        <AlertDialog.Root
          open={showHistoryRegenerateDialog}
          onOpenChange={(open) => { if (!isHistoryRegenerating) setShowHistoryRegenerateDialog(open); }}
        >
          <AlertDialog.Portal>
            <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000]" />
            <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[600px] max-w-[90vw] max-h-[85vh] overflow-y-auto z-[1001]">
              <div className="mb-4">
                <AlertDialog.Title className="text-xl font-bold">Regenerate AI outputs (History)</AlertDialog.Title>
                <AlertDialog.Description>
                  <div className="mt-3 text-sm text-gray-700 space-y-3">
                    <p>
                      This will re-generate every AI paragraph for the History report section.
                      Please review what this means before proceeding:
                    </p>
                    <ul className="list-disc pl-5 space-y-1.5">
                      <li>
                        New paragraphs will be generated using the <span className="font-semibold">latest available inputs</span>, including
                        any recent changes or additions the patient or informant has made to their questionnaires, as well as
                        your existing clinician notes and decisions.
                      </li>
                      <li>
                        The new paragraphs <span className="font-semibold">will replace the existing ones</span> -
                        including any text you have previously edited or approved.
                      </li>
                      <li>
                        After regeneration, <span className="font-semibold">these paragraphs will need to be re-reviewed and re-approved</span> before
                        the report section can be finalised.
                      </li>
                      <li>
                        The process may take <span className="font-semibold">up to a few minutes</span>.
                      </li>
                    </ul>
                    <p className="font-semibold text-red-700">
                      This action cannot be undone.
                    </p>
                  </div>
                </AlertDialog.Description>
              </div>
              <div className="flex justify-end gap-3">
                <AlertDialog.Cancel asChild>
                  <button
                    className={`py-2 px-4 mb-2 font-semibold border border-gray-300 rounded-md ${isHistoryRegenerating ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                    disabled={isHistoryRegenerating}
                  >
                    Cancel
                  </button>
                </AlertDialog.Cancel>
                <button
                  className={`flex items-center px-4 py-2 mb-2 font-semibold rounded-md ${isHistoryRegenerating ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-red-600 text-white hover:bg-red-700'}`}
                  onClick={handleConfirmHistoryRegenerate}
                  disabled={isHistoryRegenerating}
                >
                  {isHistoryRegenerating && (
                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {isHistoryRegenerating ? 'Regenerating…' : 'Re-generate All Paragraphs'}
                </button>
              </div>
            </AlertDialog.Content>
          </AlertDialog.Portal>
        </AlertDialog.Root>
      )}

      {/* Shared embed dialog — renders the selected report PDF in an iframe.
          Reuses the ADHD DocumentEmbedDialog + fetchLatestDocumentForEmbed flow;
          clinicianId is not path-critical (the CF resolves the doc from the
          patient's documents metadata), so we pass the current user. */}
      <DocumentEmbedDialog
        isOpen={isPreviewOpen}
        onClose={handleClosePreview}
        reportStage={previewReportStage}
        clinicianId={auth.currentUser?.uid}
        patientId={patientId}
        lastGenerated={previewLastGenerated}
        displayTitle={getAutismDocumentDisplayTitle(previewReportStage)}
      />
    </Layout>
  );
}
